import React, { useMemo, useState } from 'react';
import { Stack, Typography, styled, IconButton } from '@mui/material';
import { InputSelect } from '../elements/InputSelect';
import { updateLocationFrequency } from 'services/locations';
import { useFlash } from 'contexts/flash';
import { Room } from 'types/room';
import { SelectChangeEvent } from '@mui/material/Select';
import { dropdownValueTypes } from 'const/option';
import { lowerCase, startCase } from 'lodash';
import { appendDynamicOptionForPayload, getNameOption } from 'utils/convertData';
import { DropdownOption, ListOptions } from 'app/pages/RoomReportViewPhotoPage/hook';
import { InputText } from '../elements/InputText';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { useRoomContext } from 'contexts/RoomContext';

export type TOnupdateRoom = () => void;

interface FrequencySelectorProps {
  room: Room;
  frequencyOptions: ListOptions;
  onUpdateRoom?: TOnupdateRoom;
}

export const FrequencySelector: React.FC<FrequencySelectorProps> = ({
  room,
  frequencyOptions,
  onUpdateRoom
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedRoomValue, setSelectedRoomValue] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { setFlash } = useFlash();

  const { auth } = useAuth();
  const { listOption } = useRoomContext();
  const complianceOption = useMemo(() => {
    return listOption?.users?.map((u) => ({ label: u.name, value: u.name })) || [];
  }, [listOption?.users]);

  const isCanAction = useMemo(
    () => (auth?.role ? [Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role) : false),
    [auth?.role]
  );

  const handleUpdateFrequency = async () => {
    setIsLoading(true);
    try {
      const options = appendDynamicOptionForPayload(selectedRoomValue);
      await updateLocationFrequency({
        locationId: room.details.id,
        options,
        roomCustomId: selectedRoomValue?.roomCustomId,
        roomCustomId2: selectedRoomValue?.roomCustomId2,
        complianceRepresentative: selectedRoomValue?.complianceRepresentative,
        submittedId: room.id
      });

      setIsEditing(false);

      onUpdateRoom?.();
      setFlash({
        type: 'success',
        message: 'Inspection frequency updated successfully!'
      });
    } catch (error) {
      setFlash({
        type: 'error',
        message: 'Failed to update inspection frequency. Please try again.'
      });
      // Reset to original value on error
      setIsEditing(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFrequencyChange = (event: SelectChangeEvent<unknown>, name: string) => {
    selectedRoomValue[name] = event.target.value as string;
    setSelectedRoomValue({ ...selectedRoomValue });
  };

  const isValidateInputName = (name: string) => {
    if (selectedRoomValue[name] === 'OTHER' && !selectedRoomValue[`${name}_name`]) {
      setErrors({ [`${name}_name`]: 'Please input value' });
      return false;
    }
    setErrors({ [`${name}_name`]: '' });
    return true;
  };

  const handleSave = () => {
    if (Object.keys(selectedRoomValue).some((name) => !isValidateInputName(name))) return;
    handleUpdateFrequency();
  };

  const handleCancel = () => {
    setSelectedRoomValue({});
    setIsEditing(false);
  };

  const handleEdit = () => {
    const roomLevelIssueOptions: Record<string, string> = {};

    room.options?.forEach((roomIssueOption) => {
      roomOption?.forEach((dropdownValue) => {
        const hasAssetType = dropdownValue.id.toString() === roomIssueOption.type;

        if (hasAssetType) {
          const index = dropdownValue.dropdownValues.some(
            (dropdown) => dropdown.value === roomIssueOption.value
          );

          roomLevelIssueOptions[`option_${roomIssueOption?.type}`] = index
            ? roomIssueOption?.value
            : 'OTHER';
          roomLevelIssueOptions[`option_${roomIssueOption?.type}_name`] = roomIssueOption?.value;
        }
      });
    });
    roomLevelIssueOptions.roomCustomId = room.roomCustomId || '';
    roomLevelIssueOptions.roomCustomId2 = room.roomCustomId2 || '';
    roomLevelIssueOptions.complianceRepresentative = room.complianceRepresentative || '';
    setSelectedRoomValue(roomLevelIssueOptions);
    setIsEditing(true);
  };

  const roomOption = useMemo(() => {
    const newOptions = frequencyOptions?.filter((item) =>
      item.options.some((option) => option.type === dropdownValueTypes.room)
    );
    return newOptions;
  }, [frequencyOptions]);

  const roomLevelIssueOptions = useMemo(() => {
    const roomLevelIssueOptions: Record<string, string> = {};
    const roomOptionMap: Map<string, DropdownOption> = new Map();
    roomOption?.forEach((dropdownValue) => {
      roomOptionMap.set(dropdownValue.id.toString(), dropdownValue);
    });
    room.options?.forEach((roomIssueOption) => {
      const hasAssetType = roomOptionMap.get(roomIssueOption.type);

      if (hasAssetType) {
        roomLevelIssueOptions[hasAssetType?.name] = roomIssueOption?.value;
      }
    });
    roomLevelIssueOptions.roomCustomId = room.roomCustomId || '';
    roomLevelIssueOptions.roomCustomId2 = room.roomCustomId2 || '';
    roomLevelIssueOptions.complianceRepresentative = room.complianceRepresentative || '';
    roomOptionMap.clear();
    return roomLevelIssueOptions;
  }, [
    room.options,
    roomOption,
    room.roomCustomId,
    room.roomCustomId2,
    room.complianceRepresentative
  ]);

  return (
    <FrequencyContainer>
      <div>
        {!isEditing ? (
          <FrequencyDisplayContainer>
            <FrequencyLabel>Location ID</FrequencyLabel>
            <FrequencyValue onClick={handleEdit}>
              {roomLevelIssueOptions['roomCustomId']}
            </FrequencyValue>
            {isCanAction && (
              <EditIcon onClick={handleEdit}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                    fill="#226AAA"
                  />
                </svg>
              </EditIcon>
            )}
          </FrequencyDisplayContainer>
        ) : (
          <InputText
            placeholder={`Enter Location ID`}
            title={`Location ID:`}
            inputProps={{
              value: selectedRoomValue?.roomCustomId,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                handleFrequencyChange(e, 'roomCustomId');
              }
            }}
          />
        )}

        {!isEditing ? (
          <FrequencyDisplayContainer>
            <FrequencyLabel>Location ID 2</FrequencyLabel>
            <FrequencyValue onClick={handleEdit}>
              {roomLevelIssueOptions['roomCustomId2']}
            </FrequencyValue>
            {isCanAction && (
              <EditIcon onClick={handleEdit}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                    fill="#226AAA"
                  />
                </svg>
              </EditIcon>
            )}
          </FrequencyDisplayContainer>
        ) : (
          <InputText
            placeholder={`Enter Location ID 2`}
            title={`Location ID 2:`}
            inputProps={{
              value: selectedRoomValue?.roomCustomId2,
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                handleFrequencyChange(e, 'roomCustomId2');
              }
            }}
          />
        )}

        {!isEditing ? (
          <FrequencyDisplayContainer>
            <FrequencyLabel>Compliance Representative</FrequencyLabel>
            <FrequencyValue onClick={handleEdit}>
              {roomLevelIssueOptions['complianceRepresentative']}
            </FrequencyValue>
            {isCanAction && (
              <EditIcon onClick={handleEdit}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                    fill="#226AAA"
                  />
                </svg>
              </EditIcon>
            )}
          </FrequencyDisplayContainer>
        ) : (
          <FrequencyEditContainer>
            <InputSelect
              title="Compliance Representative:"
              value={selectedRoomValue?.complianceRepresentative}
              placeholder="Select Compliance Representative"
              onChange={(e: SelectChangeEvent<unknown>) =>
                handleFrequencyChange(e, 'complianceRepresentative')
              }
              options={complianceOption.map((item) => ({
                label: item?.label,
                value: item?.value?.toString()
              }))}
              sx={{ minWidth: 200 }}
              disabled={isLoading}
            />
          </FrequencyEditContainer>
        )}

        {roomOption.map((option) =>
          !isEditing ? (
            <FrequencyDisplayContainer key={option.name}>
              <FrequencyLabel>{startCase(lowerCase(option.name))}</FrequencyLabel>
              <FrequencyValue onClick={handleEdit}>
                {roomLevelIssueOptions[option.name]}
              </FrequencyValue>
              {isCanAction && (
                <EditIcon onClick={handleEdit}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                      fill="#226AAA"
                    />
                  </svg>
                </EditIcon>
              )}
            </FrequencyDisplayContainer>
          ) : (
            <FrequencyEditContainer>
              <InputSelect
                value={selectedRoomValue?.[`option_${option.id}`]}
                title={`${startCase(lowerCase(getNameOption(option)))}:`}
                onChange={(e) => {
                  handleFrequencyChange(e, `option_${option.id}`);
                }}
                options={option?.dropdownValues?.map((item) => ({
                  label: item.value,
                  value: item.value
                }))}
                placeholder={`Select ${startCase(lowerCase(getNameOption(option)))}`}
                sx={{ minWidth: 200 }}
                disabled={isLoading}
              />
              {selectedRoomValue?.[`option_${option.id}`] === 'OTHER' && (
                <InputText
                  inputError={errors?.[`option_${option.id}_name`]}
                  placeholder={`Enter ${startCase(lowerCase(getNameOption(option)))} Name`}
                  title={`${startCase(lowerCase(getNameOption(option)))} Name:`}
                  inputProps={{
                    value: selectedRoomValue?.[`option_${option.id}_name`],
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                      handleFrequencyChange(e, `option_${option.id}_name`);
                      isValidateInputName(`option_${option.id}`);
                    }
                  }}
                />
              )}
            </FrequencyEditContainer>
          )
        )}
        {isEditing && (
          <>
            <ActionButtons>
              <ActionButton
                onClick={() => {
                  handleSave();
                }}
                variant="save"
                disabled={isLoading}>
                {isLoading ? 'Saving...' : 'Save'}
              </ActionButton>
              <ActionButton onClick={handleCancel} variant="cancel" disabled={isLoading}>
                Cancel
              </ActionButton>
            </ActionButtons>
          </>
        )}
      </div>
    </FrequencyContainer>
  );
};

const FrequencyContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 0'
}));

const FrequencyDisplayContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: '8px'
}));

const FrequencyValue = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  cursor: 'pointer',
  padding: '4px 8px',
  borderRadius: '4px',
  border: '1px solid transparent',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd'
  }
}));

const EditIcon = styled(IconButton)(() => ({
  padding: '4px',
  '&:hover': {
    backgroundColor: '#f0f8ff'
  }
}));

const FrequencyEditContainer = styled(Stack)(() => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: '12px'
}));

const ActionButtons = styled(Stack)(() => ({
  flexDirection: 'row',
  gap: '8px',
  margin: '20px auto 0'
}));

const FrequencyLabel = styled(Typography)(() => ({
  fontSize: '16px',
  fontWeight: 600,
  color: '#226AAA',
  minWidth: 'max-content'
}));

const ActionButton = styled('button')<{ variant: 'save' | 'cancel'; disabled?: boolean }>(
  ({ variant, disabled }) => ({
    padding: '6px 12px',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    minWidth: '60px',
    opacity: disabled ? 0.6 : 1,
    ...(variant === 'save' && {
      backgroundColor: '#226AAA',
      color: 'white',
      '&:hover': {
        backgroundColor: disabled ? '#226AAA' : '#1a5490'
      }
    }),
    ...(variant === 'cancel' && {
      backgroundColor: '#f5f5f5',
      color: '#666',
      border: '1px solid #ddd',
      '&:hover': {
        backgroundColor: disabled ? '#f5f5f5' : '#e0e0e0'
      }
    })
  })
);
